import { supabase } from '../lib/supabase';

export interface EmailTemplate {
  id: string;
  business_id: string;
  name: string;
  subject?: string;
  preheader?: string;
  content: {
    subject: string;
    preheader: string;
    sections: Array<{
      type: string;
      content: any;
    }>;
  };
  category?: string;
  status?: 'draft' | 'active' | 'archived';
  template_type?: string;
  variables?: Record<string, any>;
  tags?: string[];
  /** Written for one person. Saved templates leave this false so they can be reused. */
  one_off?: boolean;
  usage_count?: number;
  last_used_at?: string;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
}

class EmailTemplateService {
  /**
   * Get all email templates for a business
   */
  async getTemplates(businessId: string): Promise<EmailTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }
  }

  /**
   * Get a single email template by ID
   */
  async getTemplate(templateId: string): Promise<EmailTemplate | null> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching email template:', error);
      throw error;
    }
  }

  /**
   * Create a new email template
   */
  async createTemplate(
    businessId: string,
    template: Omit<EmailTemplate, 'id' | 'business_id' | 'created_at' | 'updated_at'>
  ): Promise<EmailTemplate> {
    try {
      // Skip resource lookup entirely - these fields are causing issues
      // Build the insert data without created_by
      const insertData: any = {
        business_id: businessId,
        name: template.name,
        subject: template.content?.subject || template.subject,
        preheader: template.content?.preheader || template.preheader,
        content: template.content,
        category: template.category || 'Custom',
        status: template.status || 'draft',
        template_type: template.template_type,
        variables: template.variables,
        tags: template.tags,
        one_off: template.one_off === true,
      };

      // DO NOT include created_by - it's causing foreign key issues

      const { data, error } = await supabase
        .from('email_templates')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating email template:', error);
      throw error;
    }
  }

  /**
   * Update an existing email template
   */
  async updateTemplate(
    templateId: string,
    updates: Partial<Omit<EmailTemplate, 'id' | 'business_id' | 'created_at' | 'updated_at'>>
  ): Promise<EmailTemplate> {
    try {
      // Skip resource lookup entirely - these fields are causing issues
      const updateData: any = {
        ...updates
      };

      // DO NOT include updated_by - it's causing foreign key issues

      // Extract subject and preheader from content if provided
      if (updates.content) {
        updateData.subject = updates.content.subject;
        updateData.preheader = updates.content.preheader;
      }

      const { data, error } = await supabase
        .from('email_templates')
        .update(updateData)
        .eq('id', templateId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating email template:', error);
      throw error;
    }
  }

  /**
   * Delete an email template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting email template:', error);
      throw error;
    }
  }

  /**
   * Duplicate an email template
   */
  async duplicateTemplate(templateId: string, newName: string): Promise<EmailTemplate> {
    try {
      // First, get the original template
      const original = await this.getTemplate(templateId);
      if (!original) {
        throw new Error('Template not found');
      }

      // Create a copy with a new name
      const { id, created_at, updated_at, usage_count, last_used_at, ...templateData } = original;
      return await this.createTemplate(original.business_id, {
        ...templateData,
        name: newName,
        status: 'draft' // Reset status to draft for duplicates
      });
    } catch (error) {
      console.error('Error duplicating email template:', error);
      throw error;
    }
  }

  /**
   * Archive an email template
   */
  async archiveTemplate(templateId: string): Promise<EmailTemplate> {
    try {
      return await this.updateTemplate(templateId, { status: 'archived' });
    } catch (error) {
      console.error('Error archiving email template:', error);
      throw error;
    }
  }

  /**
   * Activate an email template
   */
  async activateTemplate(templateId: string): Promise<EmailTemplate> {
    try {
      return await this.updateTemplate(templateId, { status: 'active' });
    } catch (error) {
      console.error('Error activating email template:', error);
      throw error;
    }
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(businessId: string, category: string): Promise<EmailTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('business_id', businessId)
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching templates by category:', error);
      throw error;
    }
  }

  /**
   * Search templates by name or subject
   */
  async searchTemplates(businessId: string, searchTerm: string): Promise<EmailTemplate[]> {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('business_id', businessId)
        .or(`name.ilike.%${searchTerm}%,subject.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching email templates:', error);
      throw error;
    }
  }

  /**
   * Track template usage
   */
  async trackTemplateUsage(templateId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('increment_email_template_usage', {
        template_id: templateId
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error tracking template usage:', error);
      // Don't throw - this is not critical
    }
  }
}

export const emailTemplateService = new EmailTemplateService();
